import { useEffect, useRef } from 'react';
import { RoomMemberEvent } from 'matrix-js-sdk';
import { useMatrixClient } from '../context/MatrixContext';
import { useModal } from '../context/ModalContext';
import { useNavigation } from '../context/NavigationContext';
import matrixService from '../services/matrixService';
import logger from '../utils/logger';

/**
 * Hook to automatically accept room invites when navigating to an invited room.
 * This hook monitors the current room ID and checks if it's an invite in the provided room list.
 * If so, it automatically calls client.joinRoom() to accept the invite.
 *
 * For DM invites (marked with is_direct=true), this hook also updates the invitee's
 * m.direct account data to ensure the room appears in their DMs list, following
 * the same pattern as Element Web.
 *
 * If the inviter has already left the room before acceptance, this hook will:
 * - Display an error modal to the user
 * - Automatically leave the invalid room
 * - Navigate away to prevent the user from being stuck in an empty room
 *
 * @param {string|null} currentRoomId - The currently selected room ID
 * @param {Array} rooms - Array of room objects with id and isInvite properties
 * @param {boolean} isCreatingNew - Whether user is in the "create new" flow
 * @param {boolean} isChannelContext - Whether this is being used in ChannelsPage (vs ConversationsPage)
 */
export const useAutoAcceptInvite = (currentRoomId, rooms, isCreatingNew = false, isChannelContext = false) => {
  const client = useMatrixClient();
  const { showError } = useModal();
  const { navigateToConversation, navigateToChannel } = useNavigation();
  const acceptingRoomsRef = useRef(new Set());

  useEffect(() => {
    if (!currentRoomId || !client || isCreatingNew) {
      return;
    }

    const acceptInvite = async () => {
      const room = rooms.find(r => r.id === currentRoomId);

      // Check if this is an invite and we're not already accepting it
      if (room && room.isInvite && !acceptingRoomsRef.current.has(currentRoomId)) {
        acceptingRoomsRef.current.add(currentRoomId);

        try {
          logger.debug(`Auto-accepting invite for room: ${currentRoomId}`);

          // Get the room object to check if this is a DM invite
          const matrixRoom = client.getRoom(currentRoomId);
          const myUserId = client.getUserId();
          let isDMInvite = false;
          let inviterUserId = null;

          if (matrixRoom && myUserId) {
            // Get the invite membership event to check if it's marked as direct
            const memberEvent = matrixRoom.currentState.getStateEvents('m.room.member', myUserId);
            if (memberEvent) {
              isDMInvite = memberEvent.getContent().is_direct === true;
              inviterUserId = memberEvent.getSender();
              logger.debug(`Invite is_direct: ${isDMInvite}, inviter: ${inviterUserId}`);
            }
          }

          // Join the room
          await client.joinRoom(currentRoomId);
          logger.debug(`Successfully joined room: ${currentRoomId}`);

          // Wait for membership to update by listening for the membership event
          // This ensures the UI updates immediately with the new membership status
          await new Promise((resolve) => {
            const checkMembership = () => {
              const updatedRoom = client.getRoom(currentRoomId);
              if (updatedRoom && updatedRoom.getMyMembership() === 'join') {
                resolve();
              }
            };

            // Check immediately in case it's already updated
            checkMembership();

            // Also listen for membership events
            const onMembershipUpdate = (event, member) => {
              if (member.roomId === currentRoomId && 
                  member.userId === client.getUserId() && 
                  member.membership === 'join') {
                client.off(RoomMemberEvent.Membership, onMembershipUpdate);
                resolve();
              }
            };

            client.on(RoomMemberEvent.Membership, onMembershipUpdate);

            // Timeout after 5 seconds as a fallback
            setTimeout(() => {
              client.off(RoomMemberEvent.Membership, onMembershipUpdate);
              resolve();
            }, 5000);
          });

          // Check if the inviter left before we accepted (room only has us as member)
          const joinedRoom = client.getRoom(currentRoomId);
          if (joinedRoom) {
            const joinedMembers = joinedRoom.getJoinedMembers();
            const myUserId = client.getUserId();
            
            // If we're the only joined member, the inviter left before we accepted
            if (joinedMembers.length === 1 && joinedMembers[0].userId === myUserId) {
              logger.warn(`Inviter left room ${currentRoomId} before invite was accepted`);
              
              // Leave the invalid room
              try {
                await matrixService.leaveRoom(currentRoomId);
                logger.debug(`Left invalid room ${currentRoomId}`);
              } catch (leaveError) {
                logger.error(`Failed to leave invalid room ${currentRoomId}:`, leaveError);
              }

              // Show error to user
              showError(
                'Room No Longer Available',
                'The person who invited you has left this conversation. The room has been removed from your list.',
              );

              // Navigate away from this room using the context parameter
              if (isChannelContext) {
                navigateToChannel(null);
              } else {
                navigateToConversation(null);
              }

              // Remove from accepting set since we've handled it
              acceptingRoomsRef.current.delete(currentRoomId);
              return;
            }
          }

          // If this was a DM invite, update m.direct account data for the invitee
          if (isDMInvite && inviterUserId) {
            try {
              await matrixService.setDMRoom(currentRoomId, inviterUserId);
              logger.debug(`Updated m.direct for DM invite from ${inviterUserId}`);
            } catch (dmError) {
              logger.error('Failed to update m.direct for DM invite:', dmError);
              // Don't fail the join if m.direct update fails
            }
          }
        } catch (error) {
          logger.error(`Failed to auto-accept invite for room ${currentRoomId}:`, error);
          
          // Check if this is a M_NOT_FOUND error (inviter left before we accepted)
          if (error.name === 'M_NOT_FOUND' || error.errcode === 'M_NOT_FOUND') {
            logger.warn(`Room ${currentRoomId} no longer exists or inviter left before acceptance`);
            
            // Try to leave the room in case we're still listed as invited
            try {
              await matrixService.leaveRoom(currentRoomId);
              logger.debug(`Left invalid/non-existent room ${currentRoomId}`);
            } catch (leaveError) {
              logger.error(`Failed to leave invalid room ${currentRoomId}:`, leaveError);
            }

            // Show error to user
            showError(
              'Room No Longer Available',
              'The person who invited you has left this conversation. The room has been removed from your list.',
            );

            // Navigate away from this room
            // Use the isChannelContext parameter to determine which page to navigate to
            if (isChannelContext) {
              navigateToChannel(null);
            } else {
              navigateToConversation(null);
            }
          }
          
          // Remove from accepting set to allow retry (or mark as handled)
          acceptingRoomsRef.current.delete(currentRoomId);
        }
      }
    };

    acceptInvite();
  }, [currentRoomId, client, rooms, isCreatingNew, isChannelContext, showError, navigateToChannel, navigateToConversation]);
};

export default useAutoAcceptInvite;
